import React, { useEffect, useState } from 'react';

import RadioList, { OptionType } from '../Common/Input/Radio/RadioList';
import BaseModal from '../Common/Modal/BaseModal';

const DISCONTINUED_REASON_OPTIONS: OptionType[] = [
  { code: 'PRICE', desc: '계약 예정입니다.' },
  { code: 'NO_INQUIRY', desc: '구매 문의가 없어요.' },
  { code: 'KEEP_USING', desc: '차량을 계속 사용하기로 했어요.' },
  { code: 'OTHER_PLATFORM', desc: '다른 플랫폼에 판매하기로 했어요.' },
  { code: 'ETC', desc: '기타' },
];

const ETC_CODE = 'ETC';
const MAX_COMMENT_LENGTH = 200;

/** 확인 시 API에 보낼 pauseReason 문자열 (선택한 사유 문구 또는 기타일 때 입력한 글) */
export interface DiscontinuedConfirmPayload {
  pauseReason: string;
}

interface DiscontinuedModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (payload: DiscontinuedConfirmPayload) => void;
}

export default function DiscontinuedModal({ isOpen, onClose, onConfirm }: DiscontinuedModalProps) {
  const [selectedReasonCode, setSelectedReasonCode] = useState<string>('');
  const [comment, setComment] = useState('');
  const [reasonError, setReasonError] = useState(false);
  const [reasonEtcError, setReasonEtcError] = useState(false);

  const handleClose = () => {
    setSelectedReasonCode('');
    setComment('');
    setReasonError(false);
    setReasonEtcError(false);
    onClose();
  };

  useEffect(() => {
    if (selectedReasonCode) {
      setReasonError(false);
      if (selectedReasonCode === ETC_CODE && comment.trim() !== '') {
        setReasonEtcError(comment.trim() === '');
      }
    }
  }, [selectedReasonCode, comment]);

  const handleConfirm = () => {
    if (!selectedReasonCode) {
      setReasonError(true);

      return;
    }
    if (selectedReasonCode === ETC_CODE && comment.trim() === '') {
      setReasonEtcError(true);
      return;
    }
    setReasonError(false);
    setReasonEtcError(false);
    const selectedOption = DISCONTINUED_REASON_OPTIONS.find((o) => o.code === selectedReasonCode);
    // 기타 선택 시: 입력한 글만 전달. 그 외: 선택한 사유 문구(desc) 전달
    const pauseReason = selectedReasonCode === ETC_CODE ? comment.trim() : selectedOption?.desc ?? '';
    onConfirm({ pauseReason });
    handleClose();
  };

  const showCommentField = selectedReasonCode === ETC_CODE;

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title="판매 중지로 상태를 변경할까요?"
      content={
        <>
          <div className="text-left px-1 w-full">
            <p className="text-red text-[14px] leading-[20px] mb-3 text-center">
              *판매 중지 처리 후<span className="sm280:block hidden"></span> 판매 중으로{' '}
              <br className="sm360:block hidden" /> 상태변경 가능합니다.
            </p>

            <p className={`font-medium text-[16px] mb-3 text-gray-9 `}>
              판매 중지 사유 <span className="text-red">(필수)</span>
            </p>
            <div className="mb-2">
              <RadioList
                name="discontinuedReason"
                list={DISCONTINUED_REASON_OPTIONS}
                value={selectedReasonCode}
                onChange={(val) => setSelectedReasonCode(val.code)}
                horizontal={false}
              />
            </div>

            {showCommentField && (
              <>
                <p className="font-medium text-gray-9 text-[16px] mb-2">
                  추가 의견 <span className="text-red">(필수)</span>
                </p>
                <div className="w-full rounded-xl border border-gray-2 bg-gray-1 px-3 py-2 focus-within:border-primary-10">
                  <textarea
                    className="w-full min-h-[80px] bg-transparent text-[16px] text-gray-8 placeholder:text-gray-5 focus:outline-none resize-none"
                    placeholder="사유를 적어주세요."
                    maxLength={MAX_COMMENT_LENGTH}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                </div>
                <div className="mt-1 text-right text-[12px] text-gray-7">
                  {comment.length} / {MAX_COMMENT_LENGTH}자
                </div>
              </>
            )}
          </div>
          {reasonError && (
            <div className="w-full text-red text-left text-[16px] leading-[20px]">사유를 선택해주세요.</div>
          )}
          {reasonEtcError && (
            <div className="w-full text-red text-left text-[16px] leading-[20px]">추가 의견을 입력해주세요.</div>
          )}
        </>
      }
      leftButtonLabel="취소"
      rightButtonLabel="확인"
      onLeftButtonClick={handleClose}
      onRightButtonClick={handleConfirm}
      paddingType="padding-top-32"
    />
  );
}
